import "twin.macro";

import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ChecklistRounded } from "@mui/icons-material";
import { Button, Chip, Menu } from "@mui/material";

import _ from "lodash";

import { Type } from "@features/ui/Type";
import { OptimizedOrganization } from "@models/Organization";
import {
  ControlledAutocompletePopper,
  ControlledCheckboxInput,
} from "@utils/forms";

import { FormData } from "./DistributorForm";

const OrgOptionsSelector = ({
  orgOptions,
  fieldName,
  inputPlaceholder,
  disabled,
}: {
  orgOptions: { id: string; name: string }[];
  fieldName: string;
  inputPlaceholder: string;
  disabled?: boolean;
}) => {
  const { setValue, control } = useFormContext();
  const fieldValues = useWatch({ name: fieldName }) ?? [];
  const options = orgOptions.filter((t) => fieldValues.includes(t.id));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleRemoveOption = (id: string) => {
    setValue(
      fieldName,
      fieldValues.filter((t) => t !== id),
      { shouldDirty: true }
    );
  };

  return (
    <>
      <div tw="flex flex-wrap gap-2">
        {!disabled && (
          <Button
            endIcon={<ChecklistRounded />}
            size="small"
            tw="font-medium bg-neutral-100 px-3 hover:bg-neutral-200"
            onClick={handleClick}
          >
            Select
          </Button>
        )}
        {_.sortBy(options, "name").map((o) => (
          <Chip
            key={o.id}
            label={o.name}
            onDelete={disabled ? undefined : () => handleRemoveOption(o.id)}
            tw="bg-neutral-200"
          />
        ))}
      </div>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <ControlledAutocompletePopper
          control={control}
          name={fieldName}
          options={orgOptions}
          multiple
          getSubmitValue={(data) =>
            data.map((t) => (typeof t === "string" ? t : t.id))
          }
          inputPlaceholder={inputPlaceholder}
          disableCloseOnSelect={false}
        />
      </Menu>
    </>
  );
};

const DistributorOrgForm = ({
  distributorOrg,
}: {
  distributorOrg: OptimizedOrganization;
}) => {
  const { control } = useFormContext();
  const distName = (
    field: keyof FormData["distributorOrganizations"][string]
  ) => `distributorOrganizations.${distributorOrg.id}.${field}`;
  return (
    // key is needed to force re-render when the distributorOrg changes, strange
    <div tw="grid xl:grid-cols-2 gap-6" key={distributorOrg.id}>
      <div tw="mt-6 grid grid-cols-[5em_1fr] items-baseline gap-6">
        <Type.H5>Brands</Type.H5>
        <OrgOptionsSelector
          orgOptions={distributorOrg.brands}
          fieldName={distName("brandIds")}
          inputPlaceholder="Search brands"
          disabled={distributorOrg.brands.length === 1}
        />

        {distributorOrg.channels.length > 0 && (
          <>
            <Type.H5>Channel</Type.H5>
            <OrgOptionsSelector
              orgOptions={distributorOrg.channels}
              fieldName={distName("channelIds")}
              inputPlaceholder="Search channels"
            />
          </>
        )}

        <Type.H5>Territories</Type.H5>
        <OrgOptionsSelector
          orgOptions={distributorOrg.territories}
          fieldName={distName("territoryIds")}
          inputPlaceholder="Search territories"
        />
      </div>
      <div>
        <Type.H5 tw="mt-4 mb-2">Checkout Options</Type.H5>
        <div tw="flex flex-col gap-x-4">
          {distributorOrg.usesStandardCheckout && (
            <ControlledCheckboxInput
              control={control}
              name={distName("allowStandardCheckout")}
              label="Allow Standard Checkout"
            />
          )}
          {!!distributorOrg.budgetLocation && (
            <ControlledCheckboxInput
              control={control}
              name={distName("allowBudgetCheckout")}
              label="Allow Budget Checkout"
            />
          )}
          {distributorOrg.usesStripe && (
            <ControlledCheckboxInput
              control={control}
              name={distName("allowStripeCheckout")}
              label="Allow Stripe Checkout"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DistributorOrgForm;
